import { Component, ElementRef, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { FeeHistoryEntry, FeesListBody, OtherFeeTerm, ParentFeesService, ReceiptRow, TermFee, TypeFee } from './parent-fees.service';
import { PaymentStage } from './parent-fees-payment.component';
import { ToWords } from 'to-words';
import CurrencyList from 'currency-list';

type FeeKind = 'term' | 'type' | 'other';

export interface FeeCard {
  key: string;
  kind: FeeKind;
  name: string;
  amount: number;
  balance: number;
  paid: number;
  discount: number;
  dueDate: string | null;
  lateFee: number;
  timeLeftToPayAgain: string | null;
  raw: TermFee | TypeFee | OtherFeeTerm;
}

// Fees — list (progress bar + term/type/other cards, view-only) and a
// detail/payment-history screen (Timeline.js), mirroring
// FeesInformation.js / FeesDetailedView.js. No payment initiation — see
// parent-fees.service.ts. Rendered conditionally inside ParentLoginComponent's
// dashboard, same pattern as the other parent-* feature components (no
// routing, request-id race guard on switch).
@Component({
  selector: 'app-parent-fees',
  templateUrl: './parent-fees.component.html',
  styleUrls: ['./parent-fees.component.css'],
})
export class ParentFeesComponent implements OnInit, OnChanges {

  @Input() studentId = '';
  @Input() classId = '';
  @Input() academicId = '';
  @Input() schoolId = '';
  @Input() studentName = '';
  // Per-school currency symbol (e.g. "$" for a USD school, "₹" default) —
  // see ParentLoginComponent.studentCurrencySymbol(). Used in place of a
  // hardcoded ₹ throughout this template so non-INR schools show correctly.
  @Input() currencySymbol = '₹';
  @Input() isSequence = false;

  loading = false;
  error = '';
  cards: FeeCard[] = [];
  paymentCards: FeeCard[] | null = null;
  otherFees: FeeCard[] = [];

  // Multi-select for "pay several fees in one transaction" (FeesInformation.js).
  // Two independent selections — term/type fees vs. other fees are
  // sequenced (and paid) separately, never as one combined ordering, per
  // the mobile app's own setValue/setOtherFeesValue split.
  selectedKeys = new Set<string>();
  selectedOtherKeys = new Set<string>();

  // Sourced from feesSettings.MOBILE_FEES_PAYMENT.isFeesModuleEnable on the
  // student record (same localStorage.studentDetails payload featureList/
  // feesSettings.MOBILE_FEES_MODULE already come from — see
  // parent-login.component.ts's isFeesEnabled for the sibling gate on the
  // whole Fees tab). This one specifically controls whether payment can be
  // INITIATED — fees can still be viewed when it's disabled, only the Pay
  // affordances are hidden/blocked. Fails open (true) if the setting is
  // missing, matching every other featureList/feesSettings gate's
  // fail-open convention.
  onlinePaymentEnabled = true;

  selectedCard: FeeCard | null = null;
  detailLoading = false;
  detailError = '';
  history: FeeHistoryEntry[] = [];

  // Set when checkBalanceAmount finds an outstanding balance from a
  // previous academic year — blocks opening any fee's detail view, per
  // FeesInformation.js's itemCheckBalanceAmount gate.
  oldDuesBlocked = false;
  balanceCheckPending = false;

  private listRequestId = 0;
  private detailRequestId = 0;

  // In-app receipt preview (small centered window). receiptInfo is NOT a
  // PDF/URL — it's an array of receipt line-item rows (one per fee item
  // paid in that transaction) that the mobile app assembles into an HTML
  // string on-device (AppStrings.FeeReceiptHTMLContent). Rendered here as
  // a real Angular template instead of a hand-built HTML-string blob;
  // "print" uses the browser's native print-to-PDF instead of
  // react-native-html-to-pdf + share sheet, since neither has a web
  // equivalent.
  receiptRows: ReceiptRow[] | null = null;
  receiptLoading = false;
  printedAt = new Date();
  @ViewChild('receiptPaper') receiptPaperRef?: ElementRef<HTMLElement>;

  constructor(private feesService: ParentFeesService) { }

  ngOnInit(): void {
    this.checkOnlinePaymentEnabled();
    if (this.studentId && this.classId) {
      this.loadList();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ((changes['studentId'] || changes['classId']) && this.studentId && this.classId) {
      this.checkOnlinePaymentEnabled();
      this.selectedCard = null;
      this.history = [];
      this.oldDuesBlocked = false;
      this.selectedKeys.clear();
      this.selectedOtherKeys.clear();
      this.loadList();
    }
  }

  // Same localStorage.studentDetails lookup parent-login.component.ts's
  // storedStudent/isFeesEnabled use — duplicated here (rather than injected)
  // because this component has no access to that component's private state,
  // and feesSettings only ever lives in localStorage, not a shared service.
  private checkOnlinePaymentEnabled(): void {
    const raw = localStorage.getItem('studentDetails');
    if (!raw) { return; }
    try {
      const parsed = JSON.parse(raw);
      const student = Array.isArray(parsed) ? parsed[0] : null;
      const feesSettings = student?.feesSettings;
      if (!Array.isArray(feesSettings)) { return; }
      const setting = feesSettings.find((s: any) => s?.SettingName === 'MOBILE_FEES_PAYMENT');
      if (!setting) { return; }
      this.onlinePaymentEnabled = setting.isFeesModuleEnable === 'ENABLED';
    } catch {
      // Keep the fail-open default on a shape mismatch.
    }
  }

  // Other fees DO contribute to the overall balance/paid/progress totals
  // (confirmed against the API reference this was built from: "Contributes
  // to the overall totalFees/paidFees progress bar") — an earlier version
  // of this code excluded them, which was wrong.
  get totalBalance(): number {
    return [...this.cards, ...this.otherFees].reduce((sum, c) => sum + Math.max(0, c.balance), 0);
  }

  get totalPaidAmount(): number {
    return [...this.cards, ...this.otherFees].reduce((sum, c) => sum + c.paid + c.discount, 0);
  }

  // paid += (Paid || 0) + (Discount || 0), summed across term/type/other
  // fees together — the exact formula from the API reference this was
  // built from, not a derived amount-minus-balance approximation.
  get paidPercent(): number {
    const all = [...this.cards, ...this.otherFees];
    const totalAmount = all.reduce((sum, c) => sum + c.amount, 0);
    if (!totalAmount) { return 0; }
    const paid = all.reduce((sum, c) => sum + c.paid + c.discount, 0);
    return Math.min(100, Math.round((paid / totalAmount) * 100));
  }

  loadList(): void {
    this.loading = true;
    this.error = '';
    // checkBalanceAmount fires on mount/refresh too, not just on a card tap
    // or the bottom Pay button — per the API reference this was built from,
    // it runs at all 4 trigger points (mount, pull-to-refresh, card tap,
    // pay tap). loadList() covers both mount and refresh since this
    // component has no separate "refresh" method.
    this.runBalanceCheck();
    const requestId = ++this.listRequestId;
    this.feesService.getFeesList(this.classId, this.studentId, this.academicId).subscribe({
      next: (res: any) => {
        if (requestId !== this.listRequestId) { return; }
        this.loading = false;
        // Unlike diary/attendance/leave-request, this endpoint's response
        // has no {status, body} wrapper — termFees/typeFees/otherFeeTerms
        // sit directly at the root (confirmed against a real response).
        const body: FeesListBody | null = res && Array.isArray(res.termFees) ? res
          : (res && res.status && res.body) ? res.body : null;
        if (body) {
          this.cards = this.buildCards(body);
          this.otherFees = this.buildOtherFeeCards(body);
        } else {
          this.cards = [];
          this.otherFees = [];
          this.error = (res && res.message) || 'Unable to load fees.';
        }
        // Drop any selected key that no longer exists (paid off / removed)
        // — a fresh load must never leave a stale selection referencing a
        // fee that isn't in the new list.
        const validKeys = new Set(this.cards.map(c => c.key));
        const validOtherKeys = new Set(this.otherFees.map(c => c.key));
        this.selectedKeys.forEach(k => { if (!validKeys.has(k)) { this.selectedKeys.delete(k); } });
        this.selectedOtherKeys.forEach(k => { if (!validOtherKeys.has(k)) { this.selectedOtherKeys.delete(k); } });
      },
      error: (err) => {
        if (requestId !== this.listRequestId) { return; }
        this.loading = false;
        this.cards = [];
        this.otherFees = [];
        this.error = (err && err.error && err.error.message) || 'Unable to load fees.';
      },
    });
  }

  private buildOtherFeeCards(body: FeesListBody): FeeCard[] {
    const other: FeeCard[] = (body.otherFeeTerms || []).map(f => ({
      key: `other-${f.pkOtherFeesId}-${f.pkOtherFeeTermId}`,
      kind: 'other', name: f.Name, amount: f.termAmount, balance: f.Balance, paid: f.Paid || 0,
      discount: f.Discount || 0, dueDate: f.termDueDate, lateFee: f.lateFee || 0,
      timeLeftToPayAgain: f.timeLeftToPayAgain || null, raw: f,
    }));
    // Its own independent sort — unpaid by due date ascending, paid pushed
    // to the bottom — same pattern as term/type fees but sorted on its own
    // (per the API reference this was built from).
    return this.sortByDueDatePaidLast(other);
  }

  private sortByDueDatePaidLast(list: FeeCard[]): FeeCard[] {
    const sorted = [...list];
    sorted.sort((a, b) => {
      const aPaid = a.balance <= 0;
      const bPaid = b.balance <= 0;
      if (aPaid !== bPaid) { return aPaid ? 1 : -1; }
      if (!a.dueDate && !b.dueDate) { return 0; }
      if (!a.dueDate) { return 1; }
      if (!b.dueDate) { return -1; }
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    });
    return sorted;
  }

  private buildCards(body: FeesListBody): FeeCard[] {
    const term: FeeCard[] = (body.termFees || []).map(f => ({
      key: `term-${f.fkFeesTermId}`,
      kind: 'term', name: f.TermName, amount: f.feesTermAmount, balance: f.Balance, paid: f.Paid || 0,
      discount: f.discount || 0, dueDate: f.DueDate, lateFee: f.lateFee || 0,
      timeLeftToPayAgain: f.timeLeftToPayAgain || null, raw: f,
    }));
    const type: FeeCard[] = (body.typeFees || []).map(f => ({
      // Bus fees can have a null/undefined fkFeesTypeId (the checkout
      // payload deliberately nulls it for bus fees — see buildLineItem in
      // parent-fees-payment.component.ts), so every bus-fee card would
      // otherwise collide on the same "type-undefined" key and end up
      // toggling each other's selection state. busTermId/busStopId
      // uniquely identify each bus fee record instead.
      key: f.isBusFee ? `type-bus-${f.busTermId}-${f.busStopId}` : `type-${f.fkFeesTypeId}`,
      kind: 'type', name: f.Name, amount: f.feesTypeAmount, balance: f.Balance, paid: f.Paid || 0,
      discount: f.Discount || 0, dueDate: f.DueDate || null, lateFee: f.lateFee || 0,
      timeLeftToPayAgain: f.timeLeftToPayAgain || null, raw: f,
    }));
    // Unpaid sorted by due date ascending, paid pushed to the bottom — per
    // the mobile app's own list ordering (term/type fees combined into one
    // sort; "other" fees get their OWN independent sort — see
    // buildOtherFeeCards/sortByDueDatePaidLast).
    return this.sortByDueDatePaidLast([...term, ...type]);
  }

  // getStatus — 4 states, per the API reference this was built from:
  // Balance<=0 -> Paid; date already passed -> Overdue; date due within the
  // next 24h -> Due; date more than 24h out -> Pending. An earlier version
  // of this only distinguished Paid/Overdue/Due (folding "more than 24h
  // away" into "due"), which collapsed two of the mobile app's 4 states
  // into one.
  statusLabel(card: FeeCard): string {
    if (card.balance <= 0) { return 'paid'; }
    if (!card.dueDate) { return 'due'; }
    const hoursUntilDue = (new Date(card.dueDate).getTime() - Date.now()) / (1000 * 60 * 60);
    if (hoursUntilDue < 0) { return 'overdue'; }
    if (hoursUntilDue <= 24) { return 'due'; }
    return 'pending';
  }

  statusChipClass(card: FeeCard): string {
    const status = this.statusLabel(card);
    if (status === 'paid') { return 'pf-chip-green'; }
    if (status === 'overdue') { return 'pf-chip-red'; }
    if (status === 'due') { return 'pf-chip-yellow'; }
    return 'pf-chip-yellow';
  }

  // Left accent border color on the card itself — separate from the chip's
  // own class so a status change never accidentally styles the wrong
  // element via a shared class name.
  cardAccentClass(card: FeeCard): string {
    const status = this.statusLabel(card);
    if (status === 'paid') { return 'pf-card-accent-green'; }
    if (status === 'overdue') { return 'pf-card-accent-red'; }
    return 'pf-card-accent-yellow';
  }

  // ---------------- List grouping (Due now / Coming up / Paid this year) ----------------
  // Term/type/other fees are combined into one unified, already-date-sorted
  // list (cards/otherFees are each independently sorted — see
  // sortByDueDatePaidLast) and then re-split purely by statusLabel(): overdue
  // + due (within 24h) -> "Due now"; pending (more than 24h out) -> "Coming
  // up"; paid -> "Paid this year". No new backend data — statusLabel()
  // already distinguishes exactly these buckets.
  private get allCards(): FeeCard[] {
    return [...this.cards, ...this.otherFees];
  }

  get dueNowCards(): FeeCard[] {
    return this.allCards.filter(c => {
      const status = this.statusLabel(c);
      return status === 'overdue' || status === 'due';
    });
  }

  get comingUpCards(): FeeCard[] {
    return this.allCards.filter(c => this.statusLabel(c) === 'pending');
  }

  get paidCards(): FeeCard[] {
    return this.allCards.filter(c => this.statusLabel(c) === 'paid');
  }

  isOtherFee(card: FeeCard): boolean {
    return card.kind === 'other';
  }

  // "Was due 11 Oct 2026, 10 days ago" / "Due 31 Oct 2026, in 10 days" —
  // template composes: duePrefixKey() + the dueDate itself (date pipe) +
  // ", " + dueSuffixKey() with dueInDaysCount() interpolated in. Deliberately
  // whole-day granularity (round), not the 24h-precision statusLabel()
  // itself uses, since this is copy for a human to read, not a status gate.
  duePrefixKey(card: FeeCard): 'wasDuePrefix' | 'duePrefix' {
    return this.isOverdueCard(card) ? 'wasDuePrefix' : 'duePrefix';
  }

  dueSuffixKey(card: FeeCard): 'dueTodayLabel' | 'inDaysSuffix' | 'daysAgoSuffix' {
    const days = this.dueInDaysCount(card);
    if (days === 0) { return 'dueTodayLabel'; }
    return this.isOverdueCard(card) ? 'daysAgoSuffix' : 'inDaysSuffix';
  }

  private isOverdueCard(card: FeeCard): boolean {
    return this.statusLabel(card) === 'overdue';
  }

  dueInDaysCount(card: FeeCard): number {
    if (!card.dueDate) { return 0; }
    const due = new Date(card.dueDate);
    due.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return Math.abs(Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24)));
  }

  // Drives the "₹500 fee and ₹100 late fee" style breakdown line in the
  // template (composed there via translate pipes, not hardcoded English
  // here) — only shown when there's an actual late fee to call out;
  // otherwise the card's own amount/balance figures already say enough.
  hasLateFeeBreakdown(card: FeeCard): boolean {
    return !!card.lateFee && card.lateFee > 0;
  }

  get dueNowTotal(): number {
    return this.dueNowCards.reduce((sum, c) => sum + c.balance + (c.lateFee > 0 ? c.lateFee : 0), 0);
  }

  get overdueTotal(): number {
    return this.dueNowCards.filter(c => this.statusLabel(c) === 'overdue').reduce((sum, c) => sum + c.balance + (c.lateFee > 0 ? c.lateFee : 0), 0);
  }

  get hasOverdue(): boolean {
    return this.dueNowCards.some(c => this.statusLabel(c) === 'overdue');
  }

  get earliestOverdueCard(): FeeCard | null {
    return this.dueNowCards.find(c => this.statusLabel(c) === 'overdue') || null;
  }

  // Checkbox only shows if: Balance > 0 && onlineFeeEnabled &&
  // isOnlinePaymentEnabled && !timeLeftToPayAgain — hidden for already-paid
  // fees, when online payment is disabled, or during a post-attempt
  // cooldown window (per the API reference this was built from).
  showCheckbox(card: FeeCard): boolean {
    return card.balance > 0 && this.onlinePaymentEnabled && !card.timeLeftToPayAgain;
  }

  isSelected(card: FeeCard, isOther: boolean): boolean {
    return (isOther ? this.selectedOtherKeys : this.selectedKeys).has(card.key);
  }

  // Exact logic from the mobile app's setValue/setOtherFeesValue: with
  // isSequence off, any card toggles freely. With it on, the selected set
  // must always be a contiguous run starting at index 0 (pay oldest first,
  // no gaps) — tapping index 0 toggles freely (and re-tapping it while
  // selected clears the WHOLE selection, since nothing after it could stay
  // validly selected without it); tapping index i>0 is ignored unless
  // index i-1 is already selected; re-tapping an already-selected index i
  // deselects it and every card after it (cascading from that point).
  // list/isOther determine which of the two independent selections
  // (term/type vs other fees) this toggle applies to — they're sequenced
  // separately, never as one combined ordering.
  toggleSelection(card: FeeCard, isOther: boolean): void {
    if (!this.showCheckbox(card)) { return; }
    const list = isOther ? this.otherFees : this.cards;
    const selected = isOther ? this.selectedOtherKeys : this.selectedKeys;
    const index = list.findIndex(c => c.key === card.key);
    if (index === -1) { return; }

    if (!this.isSequence) {
      if (selected.has(card.key)) { selected.delete(card.key); } else { selected.add(card.key); }
      return;
    }

    if (index === 0) {
      if (selected.has(card.key)) {
        selected.clear();
      } else {
        selected.add(card.key);
      }
      return;
    }

    const prevKey = list[index - 1].key;
    if (!selected.has(prevKey)) { return; }

    if (selected.has(card.key)) {
      for (let i = index; i < list.length; i++) {
        selected.delete(list[i].key);
      }
    } else {
      selected.add(card.key);
    }
  }

  get selectedCards(): FeeCard[] {
    return [
      ...this.cards.filter(c => this.selectedKeys.has(c.key)),
      ...this.otherFees.filter(c => this.selectedOtherKeys.has(c.key)),
    ];
  }

  // totalSelectedFees = sum of Balance (+ lateFee if present) across every
  // selected item, term/type and other fees combined — per
  // computeTotalsFromState in the API reference this was built from.
  get totalSelectedAmount(): number {
    return this.selectedCards.reduce((sum, c) => sum + c.balance + (c.lateFee > 0 ? c.lateFee : 0), 0);
  }

  get selectedCount(): number {
    return this.selectedKeys.size + this.selectedOtherKeys.size;
  }

  get anySelectableCards(): boolean {
    return this.cards.some(c => this.showCheckbox(c));
  }

  get anySelectableOtherFees(): boolean {
    return this.otherFees.some(c => this.showCheckbox(c));
  }

  allSelected(isOther: boolean): boolean {
    const list = (isOther ? this.otherFees : this.cards).filter(c => this.showCheckbox(c));
    return list.length > 0 && list.every(c => this.isSelected(c, isOther));
  }

  // "Select all" with isSequence on just selects the full contiguous run
  // from index 0 (there's no valid way to select everything out of order),
  // "deselect all" always just clears the relevant set.
  toggleSelectAll(isOther: boolean): void {
    const list = (isOther ? this.otherFees : this.cards).filter(c => this.showCheckbox(c));
    const selected = isOther ? this.selectedOtherKeys : this.selectedKeys;
    if (this.allSelected(isOther)) {
      selected.clear();
      return;
    }
    if (!this.isSequence) {
      list.forEach(c => selected.add(c.key));
      return;
    }
    selected.clear();
    for (const c of list) {
      selected.add(c.key);
    }
  }

  get hasSelection(): boolean {
    return this.selectedKeys.size > 0 || this.selectedOtherKeys.size > 0;
  }

  // mobileNumber localStorage key holds the email address instead when the
  // parent logged in with email (see ParentLoginComponent.onLoginSuccess())
  // — same guard as ParentLoginComponent.fetchKidDetails() so an email
  // string never gets sent where the backend expects a phone number.
  private get mobileNumber(): string {
    if (localStorage.getItem('userLoginedMode') !== 'mobile') { return ''; }
    try {
      return JSON.parse(localStorage.getItem('mobileNumber') || '""');
    } catch {
      return '';
    }
  }

  // checkBalanceAmount — gates navigation to a fee's detail view AND to
  // checkout with the same "old academic year dues owed" check, fired at
  // all 4 trigger points the API reference this was built from documents:
  // mount, refresh (loadList()), card tap (openDetail), and the bottom Pay
  // button tap (payTotal). onBlocked/onClear let each call site do its own
  // thing once the check resolves (open the detail view, proceed to
  // checkout, or — for the silent mount/refresh check — nothing at all
  // beyond surfacing the blocking banner).
  private runBalanceCheck(onClear?: () => void): void {
    this.oldDuesBlocked = false;
    this.balanceCheckPending = true;
    this.feesService.checkBalanceAmount(this.classId, this.studentId, this.academicId).subscribe({
      next: (res: any) => {
        this.balanceCheckPending = false;
        const termBalance = res?.termFees?.Balance || 0;
        const typeBalance = res?.typeFees?.Balance || 0;
        if (termBalance + typeBalance > 0) {
          this.oldDuesBlocked = true;
          return;
        }
        onClear?.();
      },
      // Fail open — a failed pre-check shouldn't itself block navigation,
      // only a confirmed positive balance does.
      error: () => {
        this.balanceCheckPending = false;
        onClear?.();
      },
    });
  }

  openDetail(card: FeeCard): void {
    this.runBalanceCheck(() => {
      this.selectedCard = card;
      this.loadHistory(card);
    });
  }

  closeDetail(): void {
    this.selectedCard = null;
    this.history = [];
  }

  // payFees()'s exact gate from FeesDetailedView.js: allowed = feesIndex===0
  // || !isSequence. The card's index is looked up in whichever of the two
  // independently-sequenced lists (term/type vs other fees) it belongs to —
  // same split used everywhere else in this component. Governs only the
  // single-fee "Pay" button on the detail screen; the list's own multi-select
  // checkboxes already enforce the same contiguous-run rule themselves (see
  // toggleSelection), so payTotal() never needs this check.
  canPayFromDetail(card: FeeCard): boolean {
    if (!this.isSequence) { return true; }
    const list = card.kind === 'other' ? this.otherFees : this.cards;
    const index = list.findIndex(c => c.key === card.key);
    return index <= 0;
  }

  // Called both from a single fee's detail screen (one-item array) and from
  // the sticky "Pay total" bar (this.selectedCards, potentially several
  // items) — continueToPay in the mobile app sends every currently
  // selected fee in one combined payment request either way.
  openPayment(cards: FeeCard[]): void {
    if (!cards.length || !this.onlinePaymentEnabled) { return; }
    this.paymentCards = cards;
  }

  // Mirrors payFees()'s Alert path — a no-op click if the gate fails.
  payFromDetail(card: FeeCard): void {
    if (!this.canPayFromDetail(card)) { return; }
    this.openPayment([card]);
  }

  payTotal(): void {
    const cards = this.selectedCards;
    this.runBalanceCheck(() => this.openPayment(cards));
  }

  closePayment(): void {
    this.paymentCards = null;
  }

  // (paid) fires alongside (closed) — see ParentFeesPaymentComponent.close()
  // — only once the user has actually seen and dismissed the payment
  // modal, not the instant the payment verifies. closePayment() (bound to
  // (closed)) already clears paymentCards, so this just reloads and, for a
  // real payment attempt (not just backing out of gateway selection),
  // returns the user to the fee list — the confirmation they just saw is
  // enough context, no need to keep them on the detail screen.
  onPaid(stage: PaymentStage): void {
    // Reload so the fee's status/balance reflects the just-completed
    // payment instead of looking stale — the mobile app itself doesn't do
    // this reliably on any of its 3 payment paths (a known gap flagged in
    // the API reference this was built from).
    this.loadList();
    if (stage === 'success' || stage === 'failed' || stage === 'instamojo') {
      this.selectedKeys.clear();
      this.selectedOtherKeys.clear();
      this.closeDetail();
    } else if (this.selectedCard) {
      this.loadHistory(this.selectedCard);
    }
  }

  dismissOldDuesNotice(): void {
    this.oldDuesBlocked = false;
  }

  private loadHistory(card: FeeCard): void {
    this.detailLoading = true;
    this.detailError = '';
    this.history = [];
    const requestId = ++this.detailRequestId;
    const body = this.buildDetailBody(card);
    this.feesService.getFeesDetail(body).subscribe({
      next: (res: any) => {
        if (requestId !== this.detailRequestId) { return; }
        this.detailLoading = false;
        const rows = Array.isArray(res?.body) ? res.body : (Array.isArray(res) ? res : []);
        this.history = [...rows].sort((a, b) => new Date(b.CreatedDate).getTime() - new Date(a.CreatedDate).getTime());
      },
      error: () => {
        if (requestId !== this.detailRequestId) { return; }
        this.detailLoading = false;
        this.detailError = 'Unable to load payment history.';
      },
    });
  }

  // Body shape/field names confirmed against the real per-fee-kind
  // request — deliberately NOT the same shape as getFeesList's body
  // (classId isn't sent here at all; schoolId/mobileNumber are, instead).
  private buildDetailBody(card: FeeCard): Record<string, any> {
    const base = {
      studentId: this.studentId,
      academicId: this.academicId,
      schoolId: this.schoolId,
      mobileNumber: this.mobileNumber,
    };
    if (card.kind === 'term') {
      const f = card.raw as TermFee;
      return { ...base, feeTermId: f.fkFeesTermId, fkLateFeeId: f.pkLateFeesId ?? null };
    }
    if (card.kind === 'type') {
      const f = card.raw as TypeFee;
      // Bus fees route by bus fields, not fkFeesTypeId — per the mobile
      // app's own FeesDetailedView.js getFeesDetail(): feesTypeId is only
      // sent (non-null) when busStopId/busTermId are BOTH null, i.e. a
      // non-bus type fee. A bus fee's fkFeesTypeId is a shared value across
      // every bus-fee record (identifies "this is a bus fee", not which
      // specific one), so sending it instead of the real busTermId/busStopId
      // made the backend unable to find this fee's payment history at all —
      // the Payment Break-Up section always came back empty for bus fees.
      const isBusFee = !!f.isBusFee;
      return {
        ...base,
        feesTypeId: isBusFee ? null : f.fkFeesTypeId,
        busTermId: isBusFee ? (f.busTermId ?? null) : null,
        busStopId: isBusFee ? (f.busStopId ?? null) : null,
        fkLateFeeId: f.pkLateFeesId ?? null,
      };
    }
    const f = card.raw as OtherFeeTerm;
    return { ...base, fkOtherFeesId: f.pkOtherFeesId, fkOtherFeeTermId: f.pkOtherFeeTermId, fkLateFeeId: f.pkLateFeesId ?? null };
  }

  viewReceipt(entry: FeeHistoryEntry): void {
    this.receiptLoading = true;
    this.feesService.getFeeReceipt(entry.ReceiptNumber, this.academicId).subscribe({
      next: (res: any) => {
        this.receiptLoading = false;
        const rows: ReceiptRow[] = res?.body?.receiptInfo || res?.receiptInfo || [];
        this.receiptRows = Array.isArray(rows) && rows.length ? rows : null;
        this.printedAt = new Date();
      },
      error: () => {
        this.receiptLoading = false;
        /* receipt fetch is best-effort; fail silently */
      },
    });
  }

  closeReceiptViewer(): void {
    this.receiptRows = null;
  }

  // Clones the live, already-fully-rendered receipt into #pf-print-area (a
  // permanent element in index.html, kept in normal layout via visibility
  // rather than display:none — see styles.css — so its content is never
  // subject to a layout/paint race right before the synchronous
  // window.print() call). The global print rule hides every other body
  // child, leaving just this clone. requestAnimationFrame gives the browser
  // one paint cycle to lay out the freshly-inserted clone before printing.
  printReceipt(): void {
    const source = this.receiptPaperRef?.nativeElement;
    const printArea = document.getElementById('pf-print-area');
    if (!source || !printArea) {
      window.print();
      return;
    }
    printArea.innerHTML = '';
    printArea.appendChild(source.cloneNode(true));
    requestAnimationFrame(() => {
      // Clear only after printing finishes: Chrome builds the preview after print() returns,
      // so emptying the area straight away printed a blank page.
      window.addEventListener('afterprint', () => { printArea.innerHTML = ''; }, { once: true });
      window.print();
    });
  }

  get receiptTotalActual(): number {
    return (this.receiptRows || []).reduce((sum, r) => sum + (r.ActualAmount || 0), 0);
  }

  get receiptTotalDiscount(): number {
    return (this.receiptRows || []).reduce((sum, r) => sum + (r.DiscountAmount || 0), 0);
  }

  get receiptTotalPaid(): number {
    return (this.receiptRows || []).reduce((sum, r) => sum + (r.Amount || 0), 0);
  }

  get receiptTotalLateFee(): number {
    return (this.receiptRows || []).reduce((sum, r) => sum + (r.lateFees || 0), 0);
  }

  // "Net Amount" — per the API reference, this is deliberately identical to
  // receiptTotalActual (Σ ActualAmount) despite its name suggesting it adds
  // the late fee on top. Not a bug to fix here — replicated faithfully to
  // match the mobile app's own AppStrings.getTotalAmountWithLateFee.
  get receiptNetAmount(): number {
    return this.receiptTotalActual;
  }

  get receiptTotalReceived(): number {
    return this.receiptTotalPaid + this.receiptTotalLateFee;
  }

  // "Amount in Words" line on the printed receipt — same to-words library
  // already used by pending-fees-report.component.ts elsewhere in this app,
  // just driven by this receipt's own currency (rows[0].currencyType, e.g.
  // "INR") instead of the admin's locally-configured currency.
  get receiptAmountInWords(): string {
    const rows = this.receiptRows;
    if (!rows || !rows.length) { return ''; }
    try {
      const currency = CurrencyList.get(rows[0].currencyType);
      if (!currency) { return ''; }
      const toWords = new ToWords({
        converterOptions: {
          currency: true,
          currencyOptions: {
            name: currency.name,
            plural: currency.name_plural,
            symbol: currency.symbol,
            fractionalUnit: { name: currency.name, plural: currency.name_plural, symbol: currency.symbol },
          },
        },
      });
      return toWords.convert(this.receiptTotalReceived);
    } catch {
      return '';
    }
  }
}
